import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { ref, onChildAdded } from 'firebase/database'
import { captureEvent } from '@sentry/react'

//components
import Content from './Content'
import Layout from 'components/layout/Games'
import SlideLayout from 'components/layout/Games/Slide'
import ConnectGmailModal from 'components/GmailConnection/ConnectGmailModal'
//configs
import { db } from 'config/firebaseConfig'
//constants
import { ERRORS, OFFER_TYPE, ONBOARDING_STEP, REACT_NATIVE_STATUS } from 'constants/index'
//contexts
import AuthContext from 'context/AuthContext'
import LoaderContext from 'context/LoaderContext'
import HistoryContext from 'context/HistoryContext'
//hooks
import useTOSPromptConfig from 'hooks/useTOSPromptConfig'
//services
import { getFeaturedGameOffers } from 'services/userRewardsService'
//types
import { User } from 'types'
//utils
import { handleError, sendRNMessage, showTOSPromptForUser } from 'utils/utils'
import { decodeToken } from 'react-jwt'

export const PREV_ROUTES = ['/library', '/rewards', '/quests']

const GamesList = () => {
  const { user } = useContext(AuthContext)
  const { previousPage } = useContext(HistoryContext)

  const [showParseEnableModal, setshowParseEnableModal] = useState(false)

  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { isLoading, showLoader, hideLoader } = useContext(LoaderContext)

  const { config: TOSPromptConfig } = useTOSPromptConfig()

  useEffect(() => {
    sendRNMessage({
      status: REACT_NATIVE_STATUS.SEND_EVENT_APPSFLYER,
      eventName: 'games_view',
      eventValue: {},
    })
  }, [])

  useEffect(() => {
    if (user) {
      shouldShowParseEnableModal(user)

      if (window.ReactNativeWebView && showTOSPromptForUser(user, TOSPromptConfig?.updatedAt)) {
        navigate('/accept-tos')
        hideLoader()
      }

      if (!user.country) {
        return navigate(`/select-country`)
      }

      const offerActionsRef = ref(db, `offer_actions/${user?.id}`)
      onChildAdded(offerActionsRef, () => {
        queryClient.invalidateQueries({
          queryKey: 'featuredGameOffers',
        })
      })
    }
  }, [user])

  const { data: offers, isLoading: loadingFeaturedGameOffers } = useQuery(
    'featuredGameOffers',
    () => getFeaturedGameOffers(user?.id),
    {
      onError: handleError,
      enabled: !!user,
      retry: (failureCount, error) => {
        // Handle and report timeout error
        if (error.message.includes('timeout')) {
          captureEvent({
            message: ERRORS.FEATURED_GAME_TIMEOUT_ERROR,
            level: 'error',
            extra: { err: error },
          })

          return failureCount < 1 // Retry only 1 time
        }
        return failureCount < 3 // Retry up to 3 times
      },
    }
  )

  useEffect(() => {
    if (
      offers?.activatedRow.find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (offer: any) =>
          offer.offerType === OFFER_TYPE.REWARDED_PLAYTIME || offer.batchPlaytimeConfig
      )
    ) {
      sendRNMessage({
        status: REACT_NATIVE_STATUS.START_PLAYTIME_TRACKING_IN_FOREGROUND,
      })
    }
  }, [offers?.activatedRow])

  const isFetching = loadingFeaturedGameOffers

  useEffect(() => {
    isFetching ? showLoader() : hideLoader()
  }, [isFetching])

  const shouldShowParseEnableModal = (user: User) => {
    // [NS] I know I can do this in one if but I dont want to be fetching local storage on every render if the user has parsing enabled
    if (
      !user.hasParsingEnabled &&
      user.onboardingStep === ONBOARDING_STEP.COMPLETE &&
      isLessThan7DaysOld(user.createdAt)
    ) {
      const lastShown = localStorage.getItem('parseEnableModalShown')

      const shouldShowModal = lastShown
        ? // Show if it has been more than 7 days since last shown
          new Date().getTime() - parseInt(lastShown) > 7 * 24 * 60 * 60 * 1000
        : true // Show if never shown before

      if (shouldShowModal) {
        setshowParseEnableModal(true)
        localStorage.setItem('parseEnableModalShown', new Date().getTime().toString())
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isLessThan7DaysOld = (timeStamp: any) => {
    const timestampDate = new Date(timeStamp._seconds * 1000)

    const now = new Date()
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

    return timestampDate > sevenDaysAgo
  }

  return (
    <Layout isLoading={isLoading}>
      <ConnectGmailModal
        isModalDisplayed={showParseEnableModal}
        hideModal={() => {
          setshowParseEnableModal(false)
        }}
      />
      <SlideLayout
        prevRoutes={PREV_ROUTES}
        direction="right"
        swipeIn={!isLoading}
        isSwiping={previousPage?.startsWith('/games/')}
      >
        <Content isLoading={isLoading} offers={offers} previousPage={previousPage} />
      </SlideLayout>
    </Layout>
  )
}

export default GamesList
